/**
 * SalesRecipeDetail — `hustleplate.au/recipes/{slug}`
 * ───────────────────────────────────────────────────
 * Read-only SEO-friendly detail page that ships with the salespage.
 * Every interactive feature (print, save, upload your own photo, add
 * to your cookbook) bounces the visitor to hustlerecipes.au with the
 * recipe slug attached so the member app deep-links straight in.
 *
 * Layout
 *   • Hero image + title + cuisine/course/dietary pills
 *   • Two-column body — left: intro + ingredients; right: method
 *   • Sticky CTA bar at the top: "Print + save → hustlerecipes.au"
 *   • Footer: "View 2,400 more recipes" back to the portal index.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Users, ExternalLink, Printer, Heart, Camera, Sparkles, Share2, Check } from "lucide-react";
import { api } from "@/lib/api";
import SalesNav from "./SalesNav";

const MEMBER_HOST = "https://hustlerecipes.au";
// Cards live on the same backend that serves /api so the same origin is fine
// for both — but Pinterest needs an absolute URL with protocol.
const SITE_ORIGIN = (typeof window !== "undefined" ? window.location.origin : "");

/**
 * ShareMenu — small reusable share dropdown rendered next to the
 * member CTA in the sticky topbar.
 *
 * Strategy:
 *  1. If the browser supports the **Web Share API** (every modern
 *     mobile + Safari + Edge), tapping "Share" pops the OS-native sheet
 *     with Instagram / Messages / WhatsApp / AirDrop / etc.
 *  2. Otherwise we render a dropdown with Pinterest + Facebook + Twitter
 *     deep links + "Copy link" fallback.
 *  3. Either way the share asset is the freshly-rendered OG card from
 *     /api/og/recipe/{slug}.png — Instagram caches it for the
 *     story / post preview.
 */
function ShareMenu({ slug, title, intro }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${SITE_ORIGIN}/recipes/${slug}`;
  const ogImage = `${SITE_ORIGIN}/api/og/recipe/${slug}.png`;
  const shareText = title;
  const shareBody = intro ? `${title} — ${intro}` : title;

  async function tryNativeShare(e) {
    e?.preventDefault?.();
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareText,
          text: shareBody,
          url: shareUrl,
        });
        return true;
      } catch (_e) {
        // user dismissed — fall through to dropdown
      }
    }
    setOpen((v) => !v);
    return false;
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      // Safari old: prompt manually
      window.prompt("Copy this link", shareUrl);
    }
  }

  const pin = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}` +
              `&media=${encodeURIComponent(ogImage)}` +
              `&description=${encodeURIComponent(shareText)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}` +
             `&url=${encodeURIComponent(shareUrl)}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(shareBody + " " + shareUrl)}`;

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="cta-share"
        onClick={tryNativeShare}
        className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5"
        aria-label="Share recipe"
      >
        <Share2 className="h-3.5 w-3.5" /> Share
      </button>
      {open && (
        <div
          data-testid="share-menu"
          className="absolute right-0 top-full mt-2 w-56 bg-[#1A1410] border border-white/10 rounded-xl shadow-xl overflow-hidden z-50"
        >
          <ShareLink href={pin} testid="share-pinterest" label="Pin on Pinterest" />
          <ShareLink href={fb} testid="share-facebook" label="Share to Facebook" />
          <ShareLink href={tw} testid="share-twitter" label="Share on X" />
          <ShareLink href={wa} testid="share-whatsapp" label="Send on WhatsApp" />
          <button
            type="button"
            data-testid="share-copy"
            onClick={copyLink}
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 inline-flex items-center gap-2"
          >
            {copied
              ? <><Check className="h-3.5 w-3.5 text-emerald-400" /> Link copied</>
              : <>Copy link</>}
          </button>
        </div>
      )}
    </div>
  );
}

function ShareLink({ href, testid, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="block px-3 py-2 text-xs text-white hover:bg-white/10"
    >
      {label}
    </a>
  );
}


const CUISINE_LABELS = {
  italian: "Italian", french: "French", chinese: "Chinese",
  japanese: "Japanese", indian: "Indian", thai: "Thai",
  vietnamese: "Vietnamese", spanish: "Spanish", lebanese: "Lebanese",
  mexican: "Mexican", greek: "Greek", modern_aus: "Modern Australian",
};
const prettyCuisine = (c) => CUISINE_LABELS[c] || (c ? c[0].toUpperCase() + c.slice(1) : "");

export default function SalesRecipeDetail() {
  const { slug } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [sourceIframeOpen, setSourceIframeOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/hustle/recipes/${slug}`)
      .then((r) => setRecipe(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // ── Phase 25.21 — Open Graph meta tags so when this URL is pasted into
  // any social platform the OG card image is automatically picked up.
  // We inject the tags imperatively (avoiding a Helmet dependency).
  // MUST be declared before any early return so React Hooks rules hold.
  useEffect(() => {
    if (!recipe) return;
    const ogImageUrl = `${SITE_ORIGIN}/api/og/recipe/${slug}.png`;
    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
      return el;
    };
    const baseTitle = `${recipe.title} — HustleRecipes`;
    document.title = baseTitle;
    upsert('meta[property="og:title"]',       { property: "og:title", content: baseTitle });
    upsert('meta[property="og:description"]', { property: "og:description", content: recipe.intro || recipe.subtitle || "" });
    upsert('meta[property="og:image"]',       { property: "og:image", content: ogImageUrl });
    upsert('meta[property="og:url"]',         { property: "og:url", content: `${SITE_ORIGIN}/recipes/${slug}` });
    upsert('meta[property="og:type"]',        { property: "og:type", content: "article" });
    upsert('meta[name="twitter:card"]',       { name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:image"]',      { name: "twitter:image", content: ogImageUrl });
  }, [recipe, slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-[#5C625E]">Loading recipe…</div>
    );
  }
  if (notFound || !recipe) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-center px-4">
        <div>
          <h1 className="font-serif-display text-3xl">Recipe not found.</h1>
          <p className="text-[#5C625E] mt-2">It may have been retired or renamed.</p>
          <Link to="/recipes?sales=1" className="mt-5 inline-flex items-center gap-2 text-[#C05746] hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to the recipe library
          </Link>
        </div>
      </div>
    );
  }

  const memberDeepLink = `${MEMBER_HOST}/r/${slug}?ref=hp-sales`;
  const heroImg = recipe.hero_image_url || recipe.image_url || "";
  // The recipe schema groups ingredients in chapters (e.g. "For the sauce",
  // "For the pasta"). Flatten when there's only one heading-less group so
  // the page looks clean for simple recipes.
  const ingredientGroups = recipe.ingredient_groups
    || (recipe.ingredients_full ? [{ heading: "", items: recipe.ingredients_full }] : [])
    || (recipe.main_ingredients ? [{ heading: "", items: recipe.main_ingredients }] : []);
  const steps = recipe.method_steps || recipe.method || recipe.steps || recipe.instructions || [];

  return (
    <article data-testid="recipe-detail-page" className="bg-[#FDFBF7]">
      <SalesNav />
      {/* Sticky member CTA bar */}
      <div className="sticky top-0 z-30 bg-[#121614] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
          <Link
            to="/recipes?sales=1"
            data-testid="back-to-portal"
            className="inline-flex items-center gap-1.5 text-sm text-[#F2EBE0]/80 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> All recipes
          </Link>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <a
              href={memberDeepLink}
              data-testid="cta-print"
              className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5"
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </a>
            <a
              href={memberDeepLink}
              data-testid="cta-save"
              className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5"
            >
              <Heart className="h-3.5 w-3.5" /> Save
            </a>
            <a
              href={memberDeepLink}
              data-testid="cta-photo"
              className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5"
            >
              <Camera className="h-3.5 w-3.5" /> My photo
            </a>
            <ShareMenu
              slug={slug}
              title={recipe.title}
              intro={recipe.intro || recipe.subtitle || ""}
            />
            <a
              href={memberDeepLink}
              data-testid="cta-member"
              className="inline-flex items-center gap-1.5 text-xs rounded-full bg-[#C05746] hover:bg-[#A64A3C] px-3 py-1.5 font-bold"
            >
              hustlerecipes.au <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="bg-[#121614] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold">
              <span className="bg-white/10 rounded-full px-2.5 py-1">{prettyCuisine(recipe.cuisine)}</span>
              {recipe.course && <span className="bg-[#5A7247] rounded-full px-2.5 py-1">{recipe.course}</span>}
              {(recipe.dietary || []).map((d) => (
                <span key={d} className="bg-[#F9D29B] text-[#121614] rounded-full px-2.5 py-1">{d}</span>
              ))}
            </div>
            <h1
              data-testid="recipe-title"
              className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight"
            >
              {recipe.title}
            </h1>
            {recipe.intro && (
              <p className="mt-4 text-[#F2EBE0]/85 leading-relaxed">{recipe.intro}</p>
            )}
            {recipe.source_name && (
              <button
                type="button"
                data-testid="recipe-source-credit"
                onClick={() => setSourceIframeOpen(true)}
                className="mt-4 inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-3 py-1.5 rounded-full text-xs"
              >
                {recipe.source_logo && (
                  <img
                    src={recipe.source_logo}
                    alt=""
                    className="h-5 w-5 rounded-full object-cover"
                  />
                )}
                <span>Credit: {recipe.source_name}</span>
                <span className="opacity-70">· View original ↗</span>
              </button>
            )}
            <div className="mt-5 flex items-center gap-5 text-sm text-[#F2EBE0]/70">
              {recipe.cook_minutes > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {recipe.cook_minutes} min
                </span>
              )}
              {recipe.serves > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4" /> Serves {recipe.serves}
                </span>
              )}
            </div>
          </div>
          {heroImg && (
            <div className="rounded-3xl overflow-hidden aspect-[4/3] bg-black/30">
              <img
                src={heroImg}
                alt={recipe.title}
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
      </header>

      {/* Body */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
        <aside className="md:col-span-1">
          <h2 className="font-serif-display text-2xl mb-4">Ingredients</h2>
          {ingredientGroups.length === 0 ? (
            <p className="text-sm text-[#5C625E] italic">No ingredient list captured.</p>
          ) : (
            <div data-testid="recipe-ingredients" className="space-y-5">
              {ingredientGroups.map((grp, gi) => (
                <div key={gi}>
                  {grp.heading && (
                    <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247] mb-2">
                      {grp.heading}
                    </h3>
                  )}
                  <ul className="space-y-1.5 text-sm text-[#121614]">
                    {(grp.items || []).map((ing, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-2 h-1 w-1 rounded-full bg-[#5A7247] shrink-0" />
                        <span>{typeof ing === "string" ? ing : (ing.name || ing.label || "")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </aside>

        <div className="md:col-span-2">
          <h2 className="font-serif-display text-2xl mb-4">Method</h2>
          {steps.length === 0 ? (
            <p className="text-sm text-[#5C625E] italic">No method captured.</p>
          ) : (
            <ol data-testid="recipe-steps" className="space-y-4">
              {steps.map((s, i) => {
                // Schema variants: plain string, {text}, or {heading, items[]}.
                if (typeof s === "string") {
                  return (
                    <li key={i} className="flex gap-4">
                      <span className="shrink-0 h-7 w-7 rounded-full bg-[#5A7247] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <p className="text-[#121614] leading-relaxed">{s}</p>
                    </li>
                  );
                }
                if (s.items) {
                  return (
                    <li key={i}>
                      <div className="flex gap-4 items-start">
                        <span className="shrink-0 h-7 w-7 rounded-full bg-[#5A7247] text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                        <div className="space-y-2">
                          {s.heading && (
                            <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                              {s.heading}
                            </p>
                          )}
                          {s.items.map((line, li) => (
                            <p key={li} className="text-[#121614] leading-relaxed">{line}</p>
                          ))}
                        </div>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={i} className="flex gap-4">
                    <span className="shrink-0 h-7 w-7 rounded-full bg-[#5A7247] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <p className="text-[#121614] leading-relaxed">{s.text || s.description || ""}</p>
                  </li>
                );
              })}
            </ol>
          )}

          {/* Bottom member upsell */}
          <div className="mt-12 rounded-2xl bg-[#121614] text-white p-6 md:p-8">
            <div className="flex items-center gap-2 text-[#F9D29B] text-xs uppercase tracking-[0.25em] font-bold">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} /> Unlock more
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl mt-3">
              Print this card. Snap your own photo. Save it forever.
            </h3>
            <p className="mt-2 text-[#F2EBE0]/80">
              Hustle Members get the printable recipe card, can upload their own cooked-it
              photos and save to custom collections. It&apos;s free.
            </p>
            <a
              href={memberDeepLink}
              data-testid="cta-bottom-member"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#C05746] hover:bg-[#A64A3C] px-6 py-3 text-sm font-bold"
            >
              Continue on hustlerecipes.au <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <span className="text-xs text-[#F2EBE0]/60 uppercase tracking-wider">Share this recipe:</span>
              <a
                href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(`${SITE_ORIGIN}/recipes/${slug}`)}&media=${encodeURIComponent(`${SITE_ORIGIN}/api/og/recipe/${slug}.png`)}&description=${encodeURIComponent(recipe.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bottom-pin-cta"
                className="text-xs rounded-full bg-[#E60023] hover:opacity-90 text-white px-3 py-1.5"
              >
                Pin it
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(recipe.title + " " + SITE_ORIGIN + "/recipes/" + slug)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bottom-wa-cta"
                className="text-xs rounded-full bg-[#25D366] hover:opacity-90 text-white px-3 py-1.5"
              >
                WhatsApp
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${SITE_ORIGIN}/recipes/${slug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bottom-fb-cta"
                className="text-xs rounded-full bg-[#1877F2] hover:opacity-90 text-white px-3 py-1.5"
              >
                Facebook
              </a>
            </div>
          </div>
        </div>
      </section>

      {sourceIframeOpen && recipe.source_name && (
        <div
          className="hp-iframe-modal-overlay"
          onClick={() => setSourceIframeOpen(false)}
          data-testid="recipe-source-iframe-overlay"
        >
          <div className="hp-iframe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hp-iframe-modal-head">
              {recipe.source_logo && <img src={recipe.source_logo} alt="" />}
              <strong>{recipe.source_name}</strong>
              <a
                href={recipe.source_internal_url || recipe.website || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="recipe-source-open-tab"
              >
                Open in new tab ↗
              </a>
              <button
                type="button"
                className="hp-iframe-modal-close"
                aria-label="Close"
                onClick={() => setSourceIframeOpen(false)}
                data-testid="recipe-source-iframe-close"
              >
                ×
              </button>
            </div>
            <iframe
              src={recipe.source_internal_url || recipe.website || ""}
              title={`${recipe.title} on ${recipe.source_name}`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="recipe-source-iframe"
            />
          </div>
        </div>
      )}
    </article>
  );
}
